import { AnimatePresence, motion } from 'framer-motion'
import {
  ChevronDown,
  Image as ImageIcon,
  Layers,
  Loader2,
  MapPin,
  MessageSquare,
  RotateCcw,
  Search,
  X,
} from 'lucide-react'
import {
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'

import { useAuth } from '../../../Contexts/AuthContext'
import type { SmartSearchIndexStatus } from '../../../Contexts/SmartSearchContext'
import { useIsMobile } from '../../../hooks/useIsMobile'
import { useSmartSearch } from '../../../hooks/useSmartSearch'
import { useInputTheme, useTheme } from '../../../theme/hooks'
import type { SmartSearchLayerGroup } from '../search/buildUnifiedSearchGroups'
import { layerGroupToLayerHit } from '../search/buildUnifiedSearchGroups'
import { getCoordinateInputSuggestions } from '../search/coordinateInputAssist'
import type { LayerSearchFacetFilters } from '../search/filterLayerSearchIndex'
import { SMART_SEARCH_MAX_RESULTS } from '../search/filterSearchIndex'
import { SMART_SEARCH_NO_FOLDER_FACET } from '../search/folderFacetMatch'
import { pickInlineSearchCompletion } from '../search/inlineSearchCompletion'
import {
  hasPositiveTerms,
  parseAttributeQuery,
} from '../search/parseAttributeQuery'
import {
  COORDINATE_INPUT_HINT,
  parseCoordinates,
} from '../search/parseCoordinates'
import {
  filterRecentForPrefix,
  readRecentSearchQueries,
  rememberSearchQuery,
} from '../search/smartSearchRecentQueries'
import { userMatchTags } from '../search/userMatchTags'
import type {
  SmartSearchFeatureHit,
  SmartSearchHit,
  SmartSearchMessageHit,
  SmartSearchRasterHit,
} from '../types'
import SmartSearchFiltersPanel, {
  SmartSearchFiltersBody,
  SmartSearchFiltersTrigger,
} from './SmartSearchFiltersPanel'

function statusMessage(status: SmartSearchIndexStatus): string {
  switch (status) {
    case 'loading':
      return 'Indexation des couches opérationnelles…'
    case 'error':
      return 'Impossible de charger les couches pour la recherche.'
    case 'forbidden':
      return 'Accès refusé aux couches opérationnelles.'
    case 'no_permission':
      return 'La recherche nécessite la permission « couche opérationnelle ».'
    case 'idle':
      return 'Site non chargé - recherche indisponible.'
    default:
      return ''
  }
}

function layerGroupSubtitle(
  g: SmartSearchLayerGroup,
  queryHasText: boolean,
): string {
  const sharing = g.sharingKind === 'public' ? 'Publique' : 'Partagée'
  const folderLabel = g.category?.trim() ? g.category.trim() : 'Sans dossier'
  const catPart = `${sharing} · ${folderLabel}`
  const n = g.totalFeaturesInLayer
  const m = g.matchedFeatures.length
  if (!queryHasText) {
    return `${catPart} · ${n} objet${n > 1 ? 's' : ''}`
  }
  if (m === 0) return `${catPart} · Vue couche`
  if (m === n) return `${catPart} · ${m} résultat${m > 1 ? 's' : ''}`
  return `${catPart} · ${m} sur ${n}`
}

type LayerFlatRow =
  | { kind: 'group'; groupIndex: number }
  | { kind: 'feature'; groupIndex: number; hit: SmartSearchFeatureHit }

type PaletteRow =
  | { kind: 'coord'; lat: number; lng: number; label: string }
  | { kind: 'section_header'; label: string; count: number }
  | { kind: 'message_hit'; hit: SmartSearchMessageHit }
  | { kind: 'raster_hit'; hit: SmartSearchRasterHit }
  | LayerFlatRow

const RASTER_TYPE_LABEL: Record<SmartSearchRasterHit['rasterType'], string> = {
  snow_depth: 'Hauteur de neige',
  dtm: 'MNT',
  dsm: 'MNS',
  ortho_rgb: 'Ortho RGB',
  ortho_ir: 'Ortho IR',
  custom: 'Custom',
}

const RASTER_SOURCE_LABEL: Record<SmartSearchRasterHit['source'], string> = {
  lidar_pipeline: 'LiDAR',
  gis_upload: 'Upload GIS',
}

const DESKTOP_MODAL_WIDTH = '44rem'
const DESKTOP_FILTERS_RAIL_PX = 280

function SmartSearchPaletteBase() {
  const { pathname } = useLocation()
  const mapShortcutsActive = pathname === '/'

  const { theme, isDark } = useTheme()
  const inputTheme = useInputTheme()
  const {
    paletteOpen,
    closePalette,
    togglePalette,
    indexStatus,
    layerCategoryFilter,
    setLayerCategoryFilter,
    folderFacetLabels,
    hasLayersWithoutFolder,
    searchLayerGroups,
    listFeatureHitsForLayer,
    flyToHit,
    flyToLngLat,
    refreshIndex,
    explorationResetNonce,
    stationExplorationDirty,
    resetStationExploration,
    requestFeatureSearch,
    featureSearchStatus,
    rasterHits,
    messageHits,
    filters,
    setFilters,
    resetFilters,
    filtersActive,
  } = useSmartSearch()

  const isMobile = useIsMobile()
  const { user } = useAuth()

  const inputRef = useRef<HTMLInputElement>(null)
  const queryMeasureRef = useRef<HTMLSpanElement>(null)
  const ghostSuffixRef = useRef<HTMLSpanElement>(null)
  const listScrollRef = useRef<HTMLDivElement>(null)
  const rowRefs = useRef<Map<number, HTMLElement | null>>(new Map())
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [searchFocused, setSearchFocused] = useState(false)
  const [expandedLayerIds, setExpandedLayerIds] = useState<Set<number>>(
    () => new Set(),
  )
  const [browseHitCache, setBrowseHitCache] = useState<
    Map<number, SmartSearchFeatureHit[]>
  >(() => new Map())
  const [loadingLayerIds, setLoadingLayerIds] = useState<Set<number>>(
    () => new Set(),
  )
  const [inlineGhostLeftPx, setInlineGhostLeftPx] = useState(0)
  const [filtersPanelOpen, setFiltersPanelOpen] = useState(false)

  const registerRowRef = useCallback(
    (index: number, el: HTMLElement | null) => {
      if (el) rowRefs.current.set(index, el)
      else rowRefs.current.delete(index)
    },
    [],
  )

  const deferredQuery = useDeferredValue(query)

  const parsedQuery = useMemo(
    () => parseAttributeQuery(deferredQuery),
    [deferredQuery],
  )

  const queryHasText = useMemo(
    () => hasPositiveTerms(parsedQuery),
    [parsedQuery],
  )

  // Trigger server feature search when deferred query changes
  useEffect(() => {
    requestFeatureSearch(deferredQuery)
  }, [deferredQuery, requestFeatureSearch])

  // groups recompute when searchLayerGroups identity changes
  // (which happens when featureSearch state updates in the context)
  const groups = useMemo(
    () => searchLayerGroups(deferredQuery),
    [searchLayerGroups, deferredQuery],
  )

  const effectiveFeatures = useCallback(
    (g: SmartSearchLayerGroup): SmartSearchFeatureHit[] => {
      if (g.matchedFeatures.length > 0) return g.matchedFeatures
      return browseHitCache.get(g.layerId) ?? []
    },
    [browseHitCache],
  )

  const layerFlatRows = useMemo((): LayerFlatRow[] => {
    const out: LayerFlatRow[] = []
    groups.forEach((g, gi) => {
      out.push({ kind: 'group', groupIndex: gi })
      if (!expandedLayerIds.has(g.layerId)) return
      const feats = effectiveFeatures(g)
      feats.forEach(hit => out.push({ kind: 'feature', groupIndex: gi, hit }))
    })
    return out
  }, [groups, expandedLayerIds, effectiveFeatures])

  const parsedCoords = useMemo(
    () => parseCoordinates(deferredQuery.trim()),
    [deferredQuery],
  )

  const paletteRows = useMemo((): PaletteRow[] => {
    const rows: PaletteRow[] = []
    if (parsedCoords) {
      rows.push({
        kind: 'coord',
        lat: parsedCoords.lat,
        lng: parsedCoords.lng,
        label: `${parsedCoords.lat.toFixed(5)}, ${parsedCoords.lng.toFixed(5)}`,
      })
    }

    const showMessages = filters.types.includes('message')
    const showLayers =
      filters.types.includes('vector_layer') ||
      filters.types.includes('vector_feature')
    const showRasters = filters.types.includes('raster')

    if (showMessages && messageHits.length > 0) {
      rows.push({
        kind: 'section_header',
        label: 'Messages',
        count: messageHits.length,
      })
      messageHits.forEach(hit => rows.push({ kind: 'message_hit', hit }))
    }

    if (showLayers && layerFlatRows.length > 0) {
      rows.push({
        kind: 'section_header',
        label: 'Couches SIG',
        count: groups.length,
      })
      rows.push(...layerFlatRows)
    }

    if (showRasters && rasterHits.length > 0) {
      rows.push({
        kind: 'section_header',
        label: 'Rasters',
        count: rasterHits.length,
      })
      rasterHits.forEach(hit => rows.push({ kind: 'raster_hit', hit }))
    }

    return rows
  }, [
    parsedCoords,
    layerFlatRows,
    messageHits,
    rasterHits,
    groups.length,
    filters.types,
  ])

  const recentQueries = useMemo(
    () => (paletteOpen ? readRecentSearchQueries(user?.resort_id) : []),
    [paletteOpen, user?.resort_id],
  )

  const recentShown = useMemo(
    () => filterRecentForPrefix(recentQueries, query),
    [recentQueries, query],
  )

  const coordSuggestions = useMemo(
    () => getCoordinateInputSuggestions(query),
    [query],
  )

  const inlineCompletion = useMemo(
    () => pickInlineSearchCompletion(query, recentShown, coordSuggestions),
    [query, recentShown, coordSuggestions],
  )

  const inputTextClasses = isMobile
    ? 'text-[16px] font-medium leading-snug'
    : 'text-[15px] font-medium leading-snug'

  useLayoutEffect(() => {
    const input = inputRef.current
    const measure = queryMeasureRef.current
    const ghost = ghostSuffixRef.current
    if (!input || !measure) return

    const cs = window.getComputedStyle(input)
    const fontSync = [
      'fontFamily',
      'fontSize',
      'fontWeight',
      'fontStyle',
      'letterSpacing',
      'fontVariantNumeric',
      'lineHeight',
    ] as const
    for (const p of fontSync) {
      measure.style[p] = cs[p]
      if (ghost) ghost.style[p] = cs[p]
    }

    const padLeft = parseFloat(cs.paddingLeft) || 0
    setInlineGhostLeftPx(padLeft + measure.offsetWidth)
  }, [query, isMobile, inlineCompletion?.suffix])

  const navigateToLngLat = useCallback(
    (lng: number, lat: number) => {
      rememberSearchQuery(user?.resort_id, query.trim())
      flyToLngLat(lng, lat)
    },
    [user?.resort_id, query, flyToLngLat],
  )

  const navigateToHit = useCallback(
    (hit: SmartSearchHit) => {
      rememberSearchQuery(user?.resort_id, query.trim())
      flyToHit(hit)
    },
    [user?.resort_id, query, flyToHit],
  )

  const applySuggestionText = useCallback((text: string) => {
    setQuery(text)
    requestAnimationFrame(() => inputRef.current?.focus())
  }, [])

  const acceptInlineCompletion = useCallback(() => {
    if (!inlineCompletion) return
    applySuggestionText(inlineCompletion.full)
  }, [inlineCompletion, applySuggestionText])

  const toggleExpand = useCallback(
    (layerId: number) => {
      const opening = !expandedLayerIds.has(layerId)
      const g = groups.find(x => x.layerId === layerId)
      setExpandedLayerIds(prev => {
        const next = new Set(prev)
        if (next.has(layerId)) next.delete(layerId)
        else next.add(layerId)
        return next
      })
      if (opening && g && g.matchedFeatures.length === 0) {
        // Async expansion: load features from server
        if (browseHitCache.has(layerId)) return
        setLoadingLayerIds(prev => {
          const next = new Set(prev)
          next.add(layerId)
          return next
        })
        void (async () => {
          try {
            const hits = await listFeatureHitsForLayer(layerId)
            setBrowseHitCache(m => {
              const n = new Map(m)
              n.set(layerId, hits)
              return n
            })
          } finally {
            setLoadingLayerIds(prev => {
              const next = new Set(prev)
              next.delete(layerId)
              return next
            })
          }
        })()
      }
    },
    [expandedLayerIds, groups, listFeatureHitsForLayer, browseHitCache],
  )

  useEffect(() => {
    setBrowseHitCache(new Map())
    setLoadingLayerIds(new Set())
  }, [deferredQuery, layerCategoryFilter])

  useEffect(() => {
    if (!queryHasText) {
      setExpandedLayerIds(new Set())
      return
    }
    setExpandedLayerIds(() => {
      const next = new Set<number>()
      for (const g of groups) {
        if (g.matchedFeatures.length > 0) next.add(g.layerId)
      }
      return next
    })
  }, [queryHasText, groups, deferredQuery, layerCategoryFilter])

  useEffect(() => {
    setSelectedIndex(0)
  }, [deferredQuery, layerCategoryFilter])

  useEffect(() => {
    if (paletteRows.length === 0) return
    setSelectedIndex(i => {
      let next = Math.min(i, paletteRows.length - 1)
      // snap forward past a section header (non-selectable)
      while (
        next < paletteRows.length &&
        paletteRows[next]?.kind === 'section_header'
      )
        next += 1
      return Math.min(next, paletteRows.length - 1)
    })
  }, [paletteRows])

  useLayoutEffect(() => {
    if (!paletteOpen || paletteRows.length === 0) return
    const row = rowRefs.current.get(selectedIndex)
    const scrollRoot = listScrollRef.current
    if (!row || !scrollRoot || !scrollRoot.contains(row)) return
    row.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior: 'auto',
    })
  }, [selectedIndex, paletteOpen])

  useEffect(() => {
    if (!paletteOpen) {
      setQuery('')
      setSelectedIndex(0)
      setSearchFocused(false)
      setExpandedLayerIds(new Set())
      setBrowseHitCache(new Map())
      setLoadingLayerIds(new Set())
      return
    }
    const delayMs = isMobile ? 180 : 0
    const id = window.setTimeout(() => inputRef.current?.focus(), delayMs)
    return () => window.clearTimeout(id)
  }, [paletteOpen, isMobile])

  useEffect(() => {
    if (explorationResetNonce === 0) return
    setQuery('')
    setSelectedIndex(0)
    setExpandedLayerIds(new Set())
    setBrowseHitCache(new Map())
    setLoadingLayerIds(new Set())
  }, [explorationResetNonce])

  useEffect(() => {
    if (!mapShortcutsActive) closePalette()
  }, [mapShortcutsActive, closePalette])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isModK =
        (e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')
      if (isModK) {
        if (!mapShortcutsActive) return
        e.preventDefault()
        togglePalette()
        return
      }

      if (!paletteOpen) return

      if (e.key === 'Escape') {
        e.preventDefault()
        closePalette()
        return
      }

      // section_header rows are non-selectable; skip past them on arrow nav.
      const isSelectable = (i: number): boolean =>
        i >= 0 &&
        i < paletteRows.length &&
        paletteRows[i].kind !== 'section_header'

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex(i => {
          if (paletteRows.length === 0) return 0
          let next = Math.min(i + 1, paletteRows.length - 1)
          while (next < paletteRows.length && !isSelectable(next)) next += 1
          return Math.min(next, paletteRows.length - 1)
        })
        return
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex(i => {
          if (paletteRows.length === 0) return 0
          let next = Math.max(i - 1, 0)
          while (next > 0 && !isSelectable(next)) next -= 1
          return next
        })
        return
      }

      if (e.key === 'Enter' && paletteRows.length > 0) {
        const row = paletteRows[selectedIndex]
        if (!row) return
        e.preventDefault()
        if (row.kind === 'coord') {
          navigateToLngLat(row.lng, row.lat)
          return
        }
        if (row.kind === 'section_header') return
        if (row.kind === 'group') {
          navigateToHit(layerGroupToLayerHit(groups[row.groupIndex]))
          return
        }
        if (row.kind === 'feature') {
          navigateToHit(row.hit)
          return
        }
        if (row.kind === 'message_hit') {
          navigateToHit(row.hit)
          return
        }
        if (row.kind === 'raster_hit') {
          navigateToHit(row.hit)
          return
        }
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [
    paletteOpen,
    closePalette,
    togglePalette,
    paletteRows,
    selectedIndex,
    navigateToHit,
    navigateToLngLat,
    mapShortcutsActive,
    groups,
  ])

  const handleBackdropClick = useCallback(() => {
    closePalette()
  }, [closePalette])

  const portalTarget = typeof document !== 'undefined' ? document.body : null

  const statusLine = statusMessage(indexStatus)

  const facetChipStyle = useCallback(
    (active: boolean) => ({
      backgroundColor: active ? theme.section.bgSubtle : 'transparent',
      color: active ? theme.text.primary : theme.text.secondary,
      borderColor: active
        ? theme.section.borderStrong ?? theme.section.border
        : theme.section.border,
      boxShadow: active
        ? isDark
          ? '0 1px 4px rgba(0,0,0,0.35)'
          : '0 1px 4px rgba(15, 23, 42, 0.08)'
        : undefined,
    }),
    [theme, isDark],
  )

  const setCategory = useCallback(
    (v: LayerSearchFacetFilters['category']) => () => setLayerCategoryFilter(v),
    [setLayerCategoryFilter],
  )

  const folderFacetChipDefs = useMemo(() => {
    const defs: {
      value: LayerSearchFacetFilters['category']
      label: string
    }[] = [{ value: null, label: 'Toutes' }]
    if (hasLayersWithoutFolder) {
      defs.push({
        value: SMART_SEARCH_NO_FOLDER_FACET,
        label: 'Sans dossier',
      })
    }
    for (const name of folderFacetLabels) {
      defs.push({ value: name, label: name })
    }
    return defs
  }, [folderFacetLabels, hasLayersWithoutFolder])

  const searchFieldShadow = searchFocused
    ? `0 0 0 3px color-mix(in srgb, ${inputTheme.borderFocus} 24%, transparent),
       0 4px 20px color-mix(in srgb, ${inputTheme.borderFocus} 12%, transparent)`
    : isDark
      ? '0 2px 14px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06)'
      : '0 2px 14px rgba(15, 23, 42, 0.07), inset 0 1px 0 rgba(255,255,255,0.95)'

  const placeholder = 'Aller à...'

  const showIndexWaitBanner =
    indexStatus !== 'ready' && paletteRows.length === 0

  const showOperationalEmpty =
    indexStatus === 'ready' && groups.length === 0 && paletteRows.length === 0

  const isSearching = featureSearchStatus === 'loading' && queryHasText

  const emptyHint = query.trim()
    ? 'Aucun résultat pour cette requête.'
    : queryHasText
      ? 'Aucun résultat.'
      : 'Sans texte : toutes les couches visibles selon les filtres. Utilisez la flèche pour lister les objets. Tapez pour affiner.'

  const footerHint = `Groupé par couche · Entrée : aller au résultat · Flèche : liste · jusqu'à ${SMART_SEARCH_MAX_RESULTS} couches`

  const footerHintShort = `Groupé par couche · jusqu'à ${SMART_SEARCH_MAX_RESULTS} couches`

  const showPaletteReset = query.trim() !== '' || stationExplorationDirty

  // lock body scroll while the palette is open (both mobile and desktop) so
  // the page underneath the blurred backdrop can no longer be scrolled.
  useEffect(() => {
    if (!paletteOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [paletteOpen])

  useEffect(() => {
    if (!paletteOpen) setFiltersPanelOpen(false)
  }, [paletteOpen])

  if (!portalTarget) return null

  return createPortal(
    <AnimatePresence>
      {paletteOpen ? (
        <motion.div
          key="smart-search-overlay"
          role="presentation"
          className={
            isMobile
              ? 'fixed inset-0 z-[200] flex h-[100dvh] max-h-[100dvh] flex-col overflow-hidden'
              : 'fixed inset-0 z-[200] flex items-start justify-center overflow-hidden px-4 pb-10 pt-[12vh]'
          }
          style={
            isMobile ? { backgroundColor: theme.background.primary } : undefined
          }
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
        >
          {!isMobile ? (
            <motion.div
              aria-hidden
              className="absolute inset-0"
              style={{
                backgroundColor: 'rgba(15, 23, 42, 0.42)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onMouseDown={handleBackdropClick}
            />
          ) : null}

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Recherche intelligente"
            className={
              isMobile
                ? 'relative z-[1] flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden rounded-none border-0 shadow-none'
                : 'relative z-[1] flex max-h-[calc(100dvh-16vh)] w-[var(--smart-search-modal-width)] max-w-[92vw] flex-row overflow-hidden rounded-[1.35rem] border shadow-2xl'
            }
            style={{
              borderColor: isMobile ? 'transparent' : theme.section.border,
              backgroundColor: theme.background.primary,
              color: theme.text.primary,
              boxShadow: isMobile
                ? 'none'
                : isDark
                  ? '0 24px 48px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255,255,255,0.06)'
                  : '0 24px 48px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(255,255,255,0.8)',
              ...(!isMobile
                ? {
                    ['--smart-search-modal-width' as string]:
                      DESKTOP_MODAL_WIDTH,
                  }
                : {}),
            }}
            initial={
              isMobile
                ? { opacity: 0 }
                : { opacity: 0, y: 22, scale: 0.94, filter: 'blur(4px)' }
            }
            animate={
              isMobile
                ? { opacity: 1 }
                : { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }
            }
            exit={
              isMobile
                ? { opacity: 0 }
                : { opacity: 0, y: 14, scale: 0.97, filter: 'blur(2px)' }
            }
            transition={
              isMobile
                ? { duration: 0.22, ease: [0.22, 1, 0.36, 1] }
                : {
                    type: 'spring',
                    damping: 28,
                    stiffness: 320,
                    mass: 0.85,
                  }
            }
            onMouseDown={e => e.stopPropagation()}
          >
            {!isMobile ? (
              <AnimatePresence initial={false}>
                {filtersPanelOpen ? (
                  <motion.aside
                    key="smart-search-filters-rail"
                    aria-label="Filtres de recherche"
                    className="flex shrink-0 flex-col overflow-hidden border-r"
                    style={{
                      borderColor: theme.section.border,
                      backgroundColor: isDark
                        ? 'rgba(255,255,255,0.02)'
                        : theme.section.bgSubtle,
                    }}
                    initial={{ width: 0, opacity: 0 }}
                    animate={{
                      width: DESKTOP_FILTERS_RAIL_PX,
                      opacity: 1,
                    }}
                    exit={{ width: 0, opacity: 0 }}
                    transition={{
                      type: 'spring',
                      damping: 30,
                      stiffness: 340,
                      mass: 0.8,
                    }}
                  >
                    <div
                      className="flex shrink-0 items-center justify-between gap-2 border-b px-3 py-2.5"
                      style={{ borderColor: theme.section.border }}
                    >
                      <span
                        className="text-[12px] font-semibold"
                        style={{ color: theme.text.primary }}
                      >
                        Filtres
                      </span>
                      <button
                        type="button"
                        className="rounded-md p-1 transition-opacity hover:opacity-70"
                        style={{ color: theme.text.secondary }}
                        aria-label="Fermer les filtres"
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => setFiltersPanelOpen(false)}
                      >
                        <X size={16} aria-hidden />
                      </button>
                    </div>
                    <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
                      <SmartSearchFiltersBody
                        filters={filters}
                        onChange={setFilters}
                        onReset={resetFilters}
                        active={filtersActive}
                        embedded
                      />
                    </div>
                  </motion.aside>
                ) : null}
              </AnimatePresence>
            ) : null}

            <div
              className={
                isMobile
                  ? 'flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden'
                  : 'relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden'
              }
            >
              {isMobile ? (
                <div
                  className="flex shrink-0 items-center justify-between gap-2 border-b px-2 pb-2"
                  style={{
                    borderColor: theme.section.border,
                    paddingTop: 'max(10px, env(safe-area-inset-top))',
                  }}
                >
                  <button
                    type="button"
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-opacity active:opacity-70"
                    style={{ color: theme.text.primary }}
                    aria-label="Fermer la recherche"
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => closePalette()}
                  >
                    <X size={22} strokeWidth={2.25} aria-hidden />
                  </button>
                  <span
                    className="min-w-0 flex-1 truncate text-center text-[15px] font-semibold tracking-tight"
                    style={{ color: theme.text.primary }}
                  >
                    Recherche
                  </span>
                  <div className="flex h-10 min-w-10 shrink-0 items-center justify-end">
                    {showPaletteReset ? (
                      <button
                        type="button"
                        className="flex h-10 w-10 items-center justify-center rounded-xl transition-opacity active:opacity-70"
                        style={{ color: theme.text.secondary }}
                        aria-label="Réinitialiser la recherche et la carte"
                        title="Effacer la recherche et recentrer la carte sur le site"
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => resetStationExploration()}
                      >
                        <RotateCcw size={18} strokeWidth={2.25} aria-hidden />
                      </button>
                    ) : (
                      <span
                        className="inline-block w-10 shrink-0"
                        aria-hidden
                      />
                    )}
                  </div>
                </div>
              ) : null}
              {showPaletteReset && !isMobile ? (
                <button
                  type="button"
                  className="absolute right-2.5 top-2.5 z-[2] inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-[11px] font-semibold transition-colors"
                  style={{
                    borderColor: theme.section.border,
                    backgroundColor: theme.section.bgSubtle,
                    color: theme.text.secondary,
                  }}
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => resetStationExploration()}
                  title="Effacer la recherche et recentrer la carte sur le site"
                >
                  <RotateCcw size={14} aria-hidden />
                  Réinitialiser
                </button>
              ) : null}
              <div
                className={`shrink-0 border-b px-3 pb-3 ${!isMobile && showPaletteReset ? 'pt-11 sm:pt-10' : 'pt-3'}`}
                style={{
                  borderColor: theme.section.border,
                  backgroundColor: theme.background.primary,
                }}
              >
                {indexStatus === 'ready' ? (
                  <div className="mb-3 flex flex-wrap items-center gap-1.5">
                    <span
                      className="w-full text-[10px] font-semibold uppercase sm:w-auto"
                      style={{ color: theme.text.tertiary }}
                    >
                      Catégorie
                    </span>
                    {folderFacetChipDefs.map(({ value, label }, chipIdx) => (
                      <button
                        key={`folder-facet-${chipIdx}`}
                        type="button"
                        className="rounded-full border px-2.5 py-1 text-[11px] font-medium transition-[background-color,border-color,transform,box-shadow] duration-150 ease-out hover:brightness-[1.05] active:scale-[0.96]"
                        style={{
                          ...facetChipStyle(
                            value === null
                              ? layerCategoryFilter === null
                              : layerCategoryFilter === value,
                          ),
                          borderWidth: 1,
                          borderStyle: 'solid',
                        }}
                        onClick={setCategory(value)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                ) : null}

                {indexStatus === 'ready' ? (
                  isMobile ? (
                    <SmartSearchFiltersPanel
                      filters={filters}
                      onChange={setFilters}
                      onReset={resetFilters}
                      active={filtersActive}
                      open={filtersPanelOpen}
                      onOpenChange={setFiltersPanelOpen}
                    />
                  ) : (
                    <div className="mb-3">
                      <SmartSearchFiltersTrigger
                        filters={filters}
                        onChange={setFilters}
                        onReset={resetFilters}
                        active={filtersActive}
                        open={filtersPanelOpen}
                        onOpenChange={setFiltersPanelOpen}
                      />
                    </div>
                  )
                ) : null}

                <div
                  className="flex items-center gap-3 rounded-2xl border px-3.5 py-2.5 transition-[border-color,box-shadow,background-color] duration-200 ease-out"
                  style={{
                    borderColor: searchFocused
                      ? inputTheme.borderFocus
                      : inputTheme.border,
                    backgroundColor: inputTheme.bg,
                    boxShadow: searchFieldShadow,
                    ['--smart-search-placeholder-color' as string]:
                      inputTheme.textPlaceholderMuted ??
                      inputTheme.textPlaceholder,
                    ['--smart-search-placeholder-tracking' as string]:
                      inputTheme.placeholderLetterSpacing,
                  }}
                >
                  <motion.span
                    aria-hidden
                    className="flex shrink-0"
                    animate={{
                      scale: isMobile || !searchFocused ? 1 : 1.08,
                      color: searchFocused
                        ? inputTheme.borderFocus
                        : theme.text.tertiary,
                    }}
                    transition={{ type: 'spring', stiffness: 400, damping: 28 }}
                  >
                    <Search size={19} strokeWidth={2.25} />
                  </motion.span>
                  <div className="relative min-w-0 flex-1 overflow-hidden">
                    <span
                      ref={queryMeasureRef}
                      aria-hidden
                      className={`invisible absolute left-0 top-0 whitespace-pre py-1 ${inputTextClasses}`}
                    >
                      {query}
                    </span>
                    {searchFocused &&
                    inlineCompletion &&
                    inlineCompletion.suffix ? (
                      <span
                        ref={ghostSuffixRef}
                        aria-hidden
                        className={`pointer-events-none absolute top-0 z-20 whitespace-pre py-1 ${inputTextClasses}`}
                        style={{
                          left: inlineGhostLeftPx,
                          color: theme.text.tertiary,
                          opacity: 0.42,
                        }}
                      >
                        {inlineCompletion.suffix}
                      </span>
                    ) : null}
                    <input
                      ref={inputRef}
                      type="text"
                      role="searchbox"
                      enterKeyHint="search"
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                      aria-label={placeholder}
                      aria-autocomplete={
                        inlineCompletion?.suffix ? 'inline' : 'none'
                      }
                      className={
                        isMobile
                          ? `relative z-10 w-full min-w-0 appearance-none border-0 bg-transparent py-1 ${inputTextClasses} text-current shadow-none outline-none ring-0 transition-[color] duration-200 focus:border-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 [&::placeholder]:text-[16px] [&::placeholder]:font-normal [&::placeholder]:leading-snug [&::placeholder]:tracking-[var(--smart-search-placeholder-tracking)] [&::placeholder]:text-[color:var(--smart-search-placeholder-color)]`
                          : `relative z-10 w-full min-w-0 appearance-none border-0 bg-transparent py-1 ${inputTextClasses} text-current shadow-none outline-none ring-0 transition-[color] duration-200 focus:border-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 [&::placeholder]:text-[15px] [&::placeholder]:font-normal [&::placeholder]:leading-snug [&::placeholder]:tracking-[var(--smart-search-placeholder-tracking)] [&::placeholder]:text-[color:var(--smart-search-placeholder-color)]`
                      }
                      style={{
                        color: inputTheme.text,
                        caretColor: inputTheme.borderFocus,
                        WebkitAppearance: 'none',
                        paddingLeft: 0,
                        paddingRight: 0,
                        margin: 0,
                        boxSizing: 'border-box',
                      }}
                      placeholder={placeholder}
                      value={query}
                      onChange={e => setQuery(e.target.value)}
                      onFocus={() => setSearchFocused(true)}
                      onBlur={() => setSearchFocused(false)}
                      onKeyDown={e => {
                        if (!inlineCompletion?.suffix) return
                        if (e.key === 'Tab' && !e.shiftKey) {
                          e.preventDefault()
                          acceptInlineCompletion()
                          return
                        }
                        if (e.key === 'ArrowRight') {
                          const el = e.currentTarget
                          const len = query.length
                          if (
                            el.selectionStart === len &&
                            el.selectionEnd === len
                          ) {
                            e.preventDefault()
                            acceptInlineCompletion()
                          }
                        }
                      }}
                    />
                  </div>
                </div>
                {recentShown.length > 0 ? (
                  <div className="mt-2 flex flex-col gap-2">
                    <div className="flex flex-wrap gap-1.5">
                      <span
                        className="w-full text-[10px] font-medium uppercase"
                        style={{ color: theme.text.tertiary }}
                      >
                        Récents
                      </span>
                      {recentShown.map(r => (
                        <button
                          key={r}
                          type="button"
                          className="max-w-full truncate rounded-lg border px-2 py-1 text-[11px] font-medium transition-[border-color,background-color] duration-200"
                          style={{
                            borderColor: theme.section.border,
                            backgroundColor: theme.section.bgSubtle,
                            color: theme.text.secondary,
                          }}
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => applySuggestionText(r)}
                          title={r}
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
                <p
                  className="mt-2.5 text-[11px] leading-snug"
                  style={{ color: theme.text.tertiary }}
                >
                  {COORDINATE_INPUT_HINT}
                </p>
              </div>

              <div
                ref={listScrollRef}
                className={
                  isMobile
                    ? 'min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-2'
                    : 'min-h-0 flex-1 overflow-y-auto px-2 py-2'
                }
              >
                {showIndexWaitBanner ? (
                  <div
                    className="rounded-xl px-3 py-8 text-center text-sm transition-opacity duration-200"
                    style={{ color: theme.text.secondary }}
                  >
                    {statusLine}
                    {(indexStatus === 'error' ||
                      indexStatus === 'forbidden') && (
                      <button
                        type="button"
                        className="mt-4 rounded-lg px-3 py-1.5 text-xs font-medium underline-offset-2 transition-opacity hover:underline"
                        style={{
                          color: theme.text.primary,
                          backgroundColor: theme.section.bgSubtle,
                        }}
                        onClick={() => void refreshIndex()}
                      >
                        Réessayer
                      </button>
                    )}
                  </div>
                ) : isSearching && groups.length === 0 ? (
                  <div
                    className="flex items-center justify-center gap-2 rounded-xl px-3 py-8 text-center text-sm transition-opacity duration-200"
                    style={{ color: theme.text.secondary }}
                  >
                    <Loader2
                      size={16}
                      strokeWidth={2.25}
                      className="animate-spin"
                      aria-hidden
                    />
                    <span>Recherche en cours…</span>
                  </div>
                ) : showOperationalEmpty ? (
                  <div
                    className="rounded-xl px-3 py-10 text-center text-sm transition-opacity duration-200"
                    style={{ color: theme.text.secondary }}
                  >
                    {emptyHint}
                  </div>
                ) : (
                  <div className="flex flex-col gap-1">
                    {isSearching ? (
                      <div
                        className="flex items-center justify-center gap-1.5 py-1 text-[11px]"
                        style={{ color: theme.text.tertiary }}
                      >
                        <Loader2
                          size={12}
                          strokeWidth={2.25}
                          className="animate-spin"
                          aria-hidden
                        />
                        <span>Recherche…</span>
                      </div>
                    ) : null}
                    {paletteRows.map((row, idx) => {
                      const sel = idx === selectedIndex
                      const rowBorder = sel
                        ? theme.border.focus
                        : theme.section.border
                      const rowBg = sel ? theme.section.bgSubtle : 'transparent'
                      const rowGlow = sel
                        ? `0 0 0 1px color-mix(in srgb, ${theme.border.focus} 35%, transparent)`
                        : undefined

                      if (row.kind === 'coord') {
                        return (
                          <button
                            key={`coord-${row.label}`}
                            ref={el => registerRowRef(idx, el)}
                            type="button"
                            className="rounded-xl border px-3 py-2.5 text-left text-[13px] font-medium transition-[border-color,box-shadow,background-color] duration-200 ease-out"
                            style={{
                              borderColor: rowBorder,
                              backgroundColor: rowBg,
                              color: theme.text.primary,
                              boxShadow: rowGlow,
                            }}
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => navigateToLngLat(row.lng, row.lat)}
                          >
                            <div className="flex items-start gap-2.5">
                              <MapPin
                                size={18}
                                strokeWidth={2.25}
                                className="mt-0.5 shrink-0"
                                style={{ color: theme.text.secondary }}
                                aria-hidden
                              />
                              <div className="min-w-0">
                                <div className="font-semibold">Coordonnées</div>
                                <div
                                  className="mt-0.5 text-[11px] font-normal leading-snug"
                                  style={{ color: theme.text.secondary }}
                                >
                                  {row.label}
                                  <span className="opacity-80">{` · lat, lng (WGS84)`}</span>
                                </div>
                              </div>
                            </div>
                          </button>
                        )
                      }

                      if (row.kind === 'feature') {
                        return (
                          <button
                            key={`${row.hit.key}-${idx}`}
                            ref={el => registerRowRef(idx, el)}
                            type="button"
                            className="rounded-xl border px-3 py-2 text-left text-[13px] font-medium transition-[border-color,box-shadow,background-color] duration-200 ease-out"
                            style={{
                              marginLeft: '1.25rem',
                              borderColor: rowBorder,
                              backgroundColor: rowBg,
                              color: theme.text.primary,
                              boxShadow: rowGlow,
                            }}
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => navigateToHit(row.hit)}
                          >
                            {row.hit.label}
                          </button>
                        )
                      }

                      if (row.kind === 'section_header') {
                        const Icon =
                          row.label === 'Messages'
                            ? MessageSquare
                            : row.label === 'Rasters'
                              ? ImageIcon
                              : Layers
                        return (
                          <div
                            key={`section-${row.label}-${idx}`}
                            ref={el => registerRowRef(idx, el)}
                            className="mt-1 flex items-center gap-1.5 px-1 pb-0.5 pt-2 text-[10px] font-semibold uppercase"
                            style={{ color: theme.text.tertiary }}
                          >
                            <Icon size={12} strokeWidth={2.5} aria-hidden />
                            <span>{row.label}</span>
                            <span
                              className="ml-0.5 rounded-md px-1.5 py-px text-[10px] font-medium normal-case"
                              style={{
                                backgroundColor: theme.section.bgSubtle,
                                color: theme.text.secondary,
                              }}
                            >
                              {row.count}
                            </span>
                          </div>
                        )
                      }

                      if (row.kind === 'message_hit') {
                        const hit = row.hit
                        const title =
                          hit.description?.trim() ||
                          hit.category?.trim() ||
                          `Message #${hit.markerId}`
                        const date = hit.createdAt
                          ? new Date(hit.createdAt).toLocaleDateString('fr-FR')
                          : null
                        return (
                          <button
                            key={hit.key}
                            ref={el => registerRowRef(idx, el)}
                            type="button"
                            className="rounded-xl border px-3 py-2 text-left text-[13px] font-medium transition-[border-color,box-shadow,background-color] duration-200 ease-out"
                            style={{
                              borderColor: rowBorder,
                              backgroundColor: rowBg,
                              color: theme.text.primary,
                              boxShadow: rowGlow,
                            }}
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => navigateToHit(hit)}
                          >
                            <div className="flex items-start gap-2.5">
                              <MessageSquare
                                size={16}
                                strokeWidth={2.25}
                                className="mt-0.5 shrink-0"
                                style={{ color: theme.text.secondary }}
                                aria-hidden
                              />
                              <div className="min-w-0 flex-1">
                                <div className="truncate font-semibold">
                                  {title}
                                </div>
                                <div
                                  className="mt-0.5 truncate text-[11px] font-normal"
                                  style={{ color: theme.text.secondary }}
                                >
                                  {hit.creatorUsername ?? 'Inconnu'}
                                  {date ? ` · ${date}` : ''}
                                </div>
                              </div>
                            </div>
                          </button>
                        )
                      }

                      if (row.kind === 'raster_hit') {
                        const hit = row.hit
                        const date = hit.createdAt
                          ? new Date(hit.createdAt).toLocaleDateString('fr-FR')
                          : null
                        return (
                          <button
                            key={hit.key}
                            ref={el => registerRowRef(idx, el)}
                            type="button"
                            className="rounded-xl border px-3 py-2 text-left text-[13px] font-medium transition-[border-color,box-shadow,background-color] duration-200 ease-out"
                            style={{
                              borderColor: rowBorder,
                              backgroundColor: rowBg,
                              color: theme.text.primary,
                              boxShadow: rowGlow,
                            }}
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => navigateToHit(hit)}
                          >
                            <div className="flex items-start gap-2.5">
                              <ImageIcon
                                size={16}
                                strokeWidth={2.25}
                                className="mt-0.5 shrink-0"
                                style={{ color: theme.text.secondary }}
                                aria-hidden
                              />
                              <div className="min-w-0 flex-1">
                                <div className="truncate font-semibold">
                                  {hit.name}
                                </div>
                                <div
                                  className="mt-0.5 truncate text-[11px] font-normal"
                                  style={{ color: theme.text.secondary }}
                                >
                                  {RASTER_TYPE_LABEL[hit.rasterType]} ·{' '}
                                  {RASTER_SOURCE_LABEL[hit.source]}
                                  {date ? ` · ${date}` : ''}
                                </div>
                              </div>
                            </div>
                          </button>
                        )
                      }

                      const g = groups[row.groupIndex]
                      const expanded = expandedLayerIds.has(g.layerId)
                      const layerHit = layerGroupToLayerHit(g)
                      const tags = userMatchTags(parsedQuery, layerHit)
                      const isLayerLoading = loadingLayerIds.has(g.layerId)

                      return (
                        <div
                          key={g.layerId}
                          ref={el => registerRowRef(idx, el)}
                          className="flex flex-col overflow-hidden rounded-xl border transition-[border-color,box-shadow,background-color] duration-200 ease-out"
                          style={{
                            borderColor: sel
                              ? theme.border.focus
                              : theme.section.border,
                            backgroundColor: sel
                              ? theme.section.bgSubtle
                              : 'transparent',
                            boxShadow: sel
                              ? `0 0 0 1px color-mix(in srgb, ${theme.border.focus} 35%, transparent), 0 2px 8px color-mix(in srgb, ${theme.border.focus} 10%, transparent)`
                              : undefined,
                          }}
                        >
                          <div className="flex items-stretch gap-0.5">
                            <button
                              type="button"
                              className="min-w-0 flex-1 px-3 py-2.5 text-left"
                              onMouseDown={e => e.preventDefault()}
                              onClick={() => navigateToHit(layerHit)}
                            >
                              <div className="truncate text-sm font-semibold tracking-tight">
                                {g.layerName}
                              </div>
                              <div
                                className="truncate text-[11px] leading-tight"
                                style={{ color: theme.text.secondary }}
                              >
                                {layerGroupSubtitle(g, queryHasText)}
                              </div>
                              {tags.length > 0 ? (
                                <div
                                  className="mt-1 flex flex-wrap gap-1"
                                  aria-label="Correspondance utilisateur"
                                >
                                  {tags.map(t => (
                                    <span
                                      key={t}
                                      className="max-w-full truncate rounded-md border px-1.5 py-px text-[10px] font-medium leading-snug"
                                      style={{
                                        color: theme.text.secondary,
                                        borderColor: theme.section.border,
                                        backgroundColor: theme.section.bgSubtle,
                                      }}
                                    >
                                      {t}
                                    </span>
                                  ))}
                                </div>
                              ) : null}
                            </button>
                            <button
                              type="button"
                              className="flex w-11 shrink-0 items-center justify-center rounded-none border-l transition-colors duration-150"
                              style={{
                                borderColor: theme.section.border,
                                color: theme.text.secondary,
                              }}
                              aria-expanded={expanded}
                              aria-label={
                                expanded
                                  ? 'Masquer les objets de la couche'
                                  : 'Afficher les objets de la couche'
                              }
                              onMouseDown={e => e.preventDefault()}
                              onClick={() => toggleExpand(g.layerId)}
                            >
                              {isLayerLoading ? (
                                <Loader2
                                  size={16}
                                  strokeWidth={2.25}
                                  className="animate-spin"
                                  aria-hidden
                                />
                              ) : (
                                <ChevronDown
                                  size={18}
                                  strokeWidth={2.25}
                                  className={`transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
                                />
                              )}
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              <div
                className="shrink-0 border-t px-3 py-2.5 text-[10px] leading-relaxed"
                style={{
                  borderColor: theme.section.border,
                  color: theme.text.tertiary,
                  backgroundColor: theme.background.primary,
                  paddingBottom: isMobile
                    ? 'max(10px, env(safe-area-inset-bottom))'
                    : undefined,
                }}
              >
                {isMobile ? footerHintShort : footerHint}
              </div>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    portalTarget,
  )
}

export default memo(SmartSearchPaletteBase)
